import moment from "moment";
import deepEqual from "deep-equal";
import queryString from "query-string";
import get from "lodash/get";

import { SORTS } from "apps/experiments/util/constants";

/**
 * Convert a unix time to a display in the format "April 3 2018, 15:30".
 *
 * @param {int} time - the unix time
 * @returns {string} - the display date
 */
export const unixTimeToDate = (time) => {
	return moment(time * 1000).format("MMM D YYYY, HH:mm");
};

/**
 * Convert a JavaScript date into a unix time.
 *
 * @param {Date} date - an instance of the JS Date class
 * @returns {int} - the unix time
 */
export const dateToUnixTime = (date) => {
	return Math.round(date.getTime() / 1000);
};

/**
 * Check a set of checkpoints to see that they all are activated to 100%. This
 * is a helper function for `getIsSimpleExperiment`.
 *
 * @param {array} checkpoints - the checkpoints to look at
 * @returns {bool} - true if all checkpoints are 100%
 */
const checkActivationsFor100 = (checkpoints) => {
	let is100 = true;

	checkpoints.forEach((checkpoint) => {
		if (checkpoint.activation_percent !== 100) {
			is100 = false;
		}
	});

	return is100;
};

/**
 * Experiments have lots of options for multiple checkpoints, checkpoints of
 * different amounts, and groups having checkpoints at different times. But 99%
 * of the time, none of that is used, and we can simplify the view.
 *
 * @param {object} experiment - the experiment, as represented in the store
 * @returns {bool} - is this experiment simple?
 */
export const getIsSimpleExperiment = (experiment) => {
	let res = true;
	let firstCheckpoints;

	if (!experiment.checkpoints || !experiment.checkpoints.groups) {
		return false;
	}

	experiment.checkpoints.groups.forEach((group, i) => {
		if (!checkActivationsFor100(group.checkpoints)) {
			res = false;
		}

		if (i === 0) {
			firstCheckpoints = group.checkpoints;

			if (firstCheckpoints.length > 1) {
				res = false;
			}

		// check that each checkpoint is the same as the first
		} else if (!deepEqual(firstCheckpoints, group.checkpoints)) {
			res = false;
		}
	});

	return res;
};

/**
 * Make a text string all-caps and without spaces. This is the convention for
 * experiment and group names.
 * @param {string} text - the input string to transform
 * @return {string} - the transformed input
 */
export const prepText = (text) => {
	return text.toUpperCase().replace(" ", "_");
};

/**
 * See if an experiment is currently running. This investigates the checkpoints
 * of an experiment to see if any are active.
 * @param {Object} checkpoints - an experiment's checkpoints
 * @return {bool} - true if the experiment is running
 */
export const getIsExperimentActive = (checkpoints) => {
	let res = false;

	if (checkpoints && checkpoints.groups) {
		checkpoints.groups.forEach((group) => {
			if (group.checkpoints.length) {
				res = true;
			}
		});
	}

	return res;
};

/**
 * Given props, shove them in the url via history.pushState
 * @param {Object} props - an object of key/values to save to the url
 */
export const saveStateToUrl = (props) => {
	const oldState = queryString.parse(window.location.search);
	const newState = {
		...oldState,
		...props,
	};

	window.history.pushState(newState, "", `?${queryString.stringify(newState)}`);
};

/**
 * Sort a list of experiments.
 * @param {Array} experiments - the list of experiments to sort
 * @param {[string]} sort - the sort, one of SORTS from util/constants
 * @return {Array} a sorted list of experiments
 */
export const sortExperiments = (experiments, sort) => {
	if (!experiments || !experiments.length) {
		return [];
	}

	const activeSort = sort && SORTS[sort] ? sort : SORTS.NAME_ASC;
	let sortFunc;

	if (activeSort === SORTS.NAME_ASC) {
		sortFunc = (a, b) => {
			if (a.name.toLowerCase() > b.name.toLowerCase()) {
				return 1;
			} else {
				return -1;
			}
		};
	} else if (activeSort === SORTS.NAME_DESC) {
		sortFunc = (a, b) => {
			if (a.name.toLowerCase() < b.name.toLowerCase()) {
				return 1;
			} else {
				return -1;
			}
		};
	} else if (activeSort === SORTS.LONGEST_RUNNING) {
		sortFunc = (a, b) => {
			const checkpointsA = get(a, "checkpoints.groups[0].checkpoints") || [];
			const checkpointsB = get(b, "checkpoints.groups[0].checkpoints") || [];
			const lastCheckpointA = checkpointsA[checkpointsA.length - 1] || {};
			const lastCheckpointB = checkpointsB[checkpointsB.length - 1] || {};
			const timeA = lastCheckpointA.activation_time || 0;
			const timeB = lastCheckpointB.activation_time || 0;

			if (timeA && timeB) {
				return timeA - timeB;
			} else if (timeA && !timeB) {
				return -1;
			} else {
				return 1;
			}
		};
	}

	return experiments.sort(sortFunc);
};



// WEBPACK FOOTER //
// ./src/apps/experiments/util/helpers.js